/**
 * ESCHER FALLS — 種明かしで**どちらへずらすか**を面ごとに選ぶ（純関数）。
 *
 * ## 何を見ているか
 * 錯視の継ぎ目では、二つの口 `a` と `b` が画面の上で重なっている。
 * 3D では `d = b - a` だけ離れていて、`d` は解いた視点の視線と平行である。
 * 視点を少しずらすと、`d` が画面の上に現れて**隙間**として見える。
 *
 * ただし隙間が**水路の向きに沿って**開くと、水路が少し長く見えるだけで
 * 「離れている」とは読めない（1面を 40° ずらしたとき、真横の眺めで一本の線に見えた）。
 * **水路に直角に開いたとき**にいちばん「宙に浮いている」と分かる。
 *
 * そこで、ずらし方の候補（円周上の8方向と、既定の向きとその鏡像）を試し、各継ぎ目の隙間のうち
 * **水路に直角な成分**を画面上で測る。いちばん見えにくい継ぎ目が最もよく見える
 * 方向を選ぶ（一つでも見えない継ぎ目があると種明かしにならないので、最小値で比べる）。
 */

import type { Vec3 } from '../core/types';
import { poseFrame } from '../core/projection';

/** 宙で繋がった継ぎ目一つ。 */
export interface RevealJoint {
  /** 水が出ていく側の口の中心。 */
  readonly a: Vec3;
  /** 水が入ってくる側の口の中心。 */
  readonly b: Vec3;
  /** 継ぎ目での水路の向き（`a` の口が向いている向き）。長さは問わない。 */
  readonly axis: Vec3;
}

/** ずらし量（ラジアン）。方位 θ と仰角 φ に足す。 */
export interface RevealOffset {
  readonly dTheta: number;
  readonly dPhi: number;
}

/** ずらす大きさ（ラジアン）。90° 刻みの視点の間を、約4分の1だけ覗く。 */
export const REVEAL_ANGLE = (25 * Math.PI) / 180;

/** 継ぎ目が見つからないときの既定（右へ、少し上から）。 */
export const DEFAULT_REVEAL_OFFSET: RevealOffset = {
  dTheta: REVEAL_ANGLE,
  dPhi: (10 * Math.PI) / 180,
};

/** 仰角の下限。これより低いと地面すれすれの眺めになり、構造が読めない。 */
const MIN_PHI = (12 * Math.PI) / 180;
/** 仰角の上限（真上）。これを越えると上下が裏返る。 */
const MAX_PHI = Math.PI / 2;

const dot = (u: Vec3, v: Vec3): number => u[0] * v[0] + u[1] * v[1] + u[2] * v[2];
const cross = (u: Vec3, v: Vec3): Vec3 => [
  u[1] * v[2] - u[2] * v[1],
  u[2] * v[0] - u[0] * v[2],
  u[0] * v[1] - u[1] * v[0],
];

/**
 * 視点 (θ, φ) から見たとき、継ぎ目の隙間のうち**水路に直角な成分**の長さ（ワールド単位）。
 * 水路が画面の奥へ向いている（画面上の長さがほぼ 0）ときは、どちらへ開いても直角なので隙間全体を返す。
 */
export function perpendicularGap(joint: RevealJoint, theta: number, phi: number): number {
  const { dir, up } = poseFrame(theta, phi);
  const right = cross(up, dir);
  const d: Vec3 = [joint.b[0] - joint.a[0], joint.b[1] - joint.a[1], joint.b[2] - joint.a[2]];
  const sx = dot(d, right);
  const sy = dot(d, up);
  const qx = dot(joint.axis, right);
  const qy = dot(joint.axis, up);
  const qLen = Math.hypot(qx, qy);
  if (qLen < 1e-6) return Math.hypot(sx, sy);
  // 隙間ベクトル s から、水路の向き q̂ に沿った成分を除いた残りの長さ。
  return Math.abs(sx * qy - sy * qx) / qLen;
}

/**
 * 面に合わせて、ずらす向きを選ぶ。
 *
 * @param theta 解いた視点の方位（ラジアン）
 * @param phi   解いた視点の仰角（ラジアン）
 * @returns 候補のうち、**最も見えにくい継ぎ目が最もよく見える**ずらし方。
 *          継ぎ目が無い・どの候補も仰角の範囲を外れるときは {@link DEFAULT_REVEAL_OFFSET}。
 */
export function chooseRevealOffset(
  joints: readonly RevealJoint[],
  theta: number,
  phi: number,
): RevealOffset {
  if (joints.length === 0) return clampDefault(phi);

  let best: RevealOffset | null = null;
  let bestMin = -1;
  let bestSum = -1;
  // **既定の向き（とその鏡像）も候補に入れる。** 円周上の8方向だけだと、既定で
  // よく効いていた面（2面・5面）がわずかに見えにくい向きへ移ることがあった。
  // 既定を含めておけば、選んだ向きが既定より悪くなることは無い。
  const candidates: RevealOffset[] = [DEFAULT_REVEAL_OFFSET];
  for (const sT of [1, -1]) {
    for (const sP of [1, -1]) {
      if (sT === 1 && sP === 1) continue;
      candidates.push({ dTheta: sT * DEFAULT_REVEAL_OFFSET.dTheta, dPhi: sP * DEFAULT_REVEAL_OFFSET.dPhi });
    }
  }
  for (let k = 0; k < 8; k += 1) {
    const a = (k * Math.PI) / 4;
    candidates.push({ dTheta: REVEAL_ANGLE * Math.cos(a), dPhi: REVEAL_ANGLE * Math.sin(a) });
  }

  for (const offset of candidates) {
    const p = phi + offset.dPhi;
    if (p < MIN_PHI || p > MAX_PHI + 1e-9) continue;

    let min = Infinity;
    let sum = 0;
    for (const j of joints) {
      const g = perpendicularGap(j, theta + offset.dTheta, p);
      min = Math.min(min, g);
      sum += g;
    }
    // 僅差なら合計の大きい方。並びが同じなら先に見た方（右寄り）を残す。
    if (min > bestMin + 1e-9 || (Math.abs(min - bestMin) <= 1e-9 && sum > bestSum + 1e-9)) {
      best = offset;
      bestMin = min;
      bestSum = sum;
    }
  }
  return best ?? clampDefault(phi);
}

/** 既定のずらし方を、仰角の範囲に収まる向きにして返す（真上なら下へ）。 */
function clampDefault(phi: number): RevealOffset {
  const up = phi + DEFAULT_REVEAL_OFFSET.dPhi <= MAX_PHI + 1e-9;
  return up ? DEFAULT_REVEAL_OFFSET : { ...DEFAULT_REVEAL_OFFSET, dPhi: -DEFAULT_REVEAL_OFFSET.dPhi };
}
